import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiBarChart2,
  FiBookOpen,
  FiBox,
  FiEdit2,
  FiLock,
  FiPlus,
  FiTrash2,
  FiTrendingUp,
} from "react-icons/fi";
import AddCourseProductModal from "../components/add-cource/AddCourseProductModal";
import CourseProductTable from "../components/CourseProductTable";
import CourseAnalyticsDrawer from "../components/add-cource/CourseAnalyticsDrawer";
import { db, storage } from "../firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  deleteDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { ref, deleteObject } from "firebase/storage";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { showToast } from "../utils/toast";
import {
  getBusinessTypeLabel,
  inferStoreBusinessType,
  summarizeStorePackages,
} from "../utils/storePackages";

const TYPE_META = {
  course: {
    label: "Courses",
    singular: "Course",
    icon: FiBookOpen,
    tint: "from-amber-100 via-orange-50 to-white",
    border: "border-amber-200",
    button: "bg-amber-600 hover:bg-amber-700",
    copy: "Build your university storefront with structured lessons and digital content.",
  },
  product: {
    label: "Products",
    singular: "Product",
    icon: FiBox,
    tint: "from-sky-100 via-cyan-50 to-white",
    border: "border-sky-200",
    button: "bg-sky-600 hover:bg-sky-700",
    copy: "Create a shopping-style store for physical or digital product selling.",
  },
  service: {
    label: "Services",
    singular: "Service",
    icon: FiTrendingUp,
    tint: "from-emerald-100 via-green-50 to-white",
    border: "border-emerald-200",
    button: "bg-emerald-600 hover:bg-emerald-700",
    copy: "Offer service packages, client work, and consulting in one branded portal.",
  },
};

export function prepareEditData(x) {
  return { id: x.id || "", ...x };
}

function hasRequiredPaymentDetails(userDoc = {}, storeDoc = {}) {
  const payment = storeDoc?.paymentDetails || {};
  const bankName = String(payment.bankName || userDoc?.bankName || "").trim();
  const accountHolder = String(
    payment.accountHolder ||
      userDoc?.accountHolder ||
      userDoc?.fullName ||
      userDoc?.displayName ||
      "",
  ).trim();
  const accountNumber = String(
    payment.accountNumber || userDoc?.bankAccount || "",
  ).trim();

  return Boolean(bankName && accountHolder && accountNumber);
}

function moneyCount(value) {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n : 0;
}

function limitForType(limits = {}, type = "") {
  if (type === "course") return moneyCount(limits.courseLimit);
  if (type === "product") return moneyCount(limits.productLimit);
  if (type === "service") return moneyCount(limits.serviceLimit);
  return 0;
}

function formatListingPrice(item = {}) {
  const raw = Number(item?.price || 0);
  if (!(raw > 0)) return "Free";
  return `LKR ${raw.toLocaleString("en-LK", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function statusTone(status = "") {
  const value = String(status || "").toLowerCase();
  if (value === "approved") return "bg-green-100 text-green-700";
  if (value === "pending") return "bg-amber-100 text-amber-800";
  if (value === "declined") return "bg-rose-100 text-rose-700";
  return "bg-slate-100 text-slate-700";
}

function getListingStock(item = {}) {
  const raw = item?.stock ?? item?.inventory;
  if (raw == null || raw === "") return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

function formatListingStock(item = {}) {
  const stock = getListingStock(item);
  if (stock == null) return "Not set";
  return stock.toLocaleString("en-LK");
}

function getWholesaleSummary(item = {}) {
  if (!item?.wholesaleEnabled) return "Retail only";
  const minQty = Number(item?.wholesaleMinQty || 0) || 0;
  const price =
    item?.wholesalePrice != null && item?.wholesalePrice !== ""
      ? Number(item.wholesalePrice)
      : null;
  if (price == null) return minQty > 0 ? `Min ${minQty} items` : "Enabled";
  return minQty > 0
    ? `Min ${minQty} · LKR ${price.toLocaleString("en-LK")}`
    : `LKR ${price.toLocaleString("en-LK")}`;
}

function isListingActive(item = {}) {
  return item?.isActive !== false;
}

function listingImage(item = {}) {
  const candidates = [
    item?.thumbnailUrl,
    item?.thumbnail,
    Array.isArray(item?.productImages)
      ? typeof item.productImages[0] === "string"
        ? item.productImages[0]
        : item.productImages[0]?.url
      : null,
    Array.isArray(item?.serviceImages)
      ? typeof item.serviceImages[0] === "string"
        ? item.serviceImages[0]
        : item.serviceImages[0]?.url
      : null,
    Array.isArray(item?.courseImages)
      ? typeof item.courseImages[0] === "string"
        ? item.courseImages[0]
        : item.courseImages[0]?.url
      : null,
  ].filter(Boolean);

  return candidates[0] || "/user-portal-logo.png";
}

function SmartImage({ src, alt }) {
  const [imageSrc, setImageSrc] = useState(src || "/user-portal-logo.png");

  useEffect(() => {
    setImageSrc(src || "/user-portal-logo.png");
  }, [src]);

  return (
    <img
      src={imageSrc}
      alt={alt}
      className="h-full w-full object-cover"
      onError={() => setImageSrc("/user-portal-logo.png")}
      referrerPolicy="no-referrer"
      draggable="false"
    />
  );
}

function TypeChoiceCard({ type, active, disabled, limit, onSelect, locked }) {
  const meta = TYPE_META[type];
  const Icon = meta.icon;
  return (
    <button
      type="button"
      onClick={() => !disabled && onSelect(type)}
      disabled={disabled}
      className={`text-left rounded-[28px] border p-6 transition-all shadow-sm bg-gradient-to-br ${meta.tint} ${meta.border} ${
        active
          ? "ring-2 ring-offset-2 ring-slate-900 shadow-xl scale-[1.01]"
          : "hover:-translate-y-0.5 hover:shadow-lg"
      } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="h-14 w-14 rounded-2xl bg-white/80 border border-white shadow-inner flex items-center justify-center">
          <Icon className="text-2xl text-slate-800" />
        </div>
        {locked ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-900 text-white px-3 py-1 text-xs font-semibold">
            <FiLock />
            Locked
          </span>
        ) : active ? (
          <span className="inline-flex items-center rounded-full bg-slate-900 text-white px-3 py-1 text-xs font-semibold">
            Selected
          </span>
        ) : null}
      </div>
      <div className="mt-6">
        <div className="text-2xl font-black text-slate-900">{meta.label}</div>
        <p className="mt-2 text-sm leading-6 text-slate-600">{meta.copy}</p>
        <div className="mt-4 text-sm font-semibold text-slate-700">
          Package capacity: {limit > 0 ? `${limit} slots` : "Unlimited"}
        </div>
      </div>
    </button>
  );
}

function ListingSlotCard({
  index,
  type,
  item,
  locked,
  onCreate,
  onEdit,
  onAnalytics,
  onToggleActive,
  onDelete,
}) {
  const meta = TYPE_META[type];
  const Icon = meta.icon;
  const isProductCard = type === "product";
  const active = isListingActive(item);

  if (!item) {
    return (
      <button
        type="button"
        onClick={onCreate}
        className="group min-h-[280px] rounded-[28px] border-2 border-dashed border-amber-200 bg-gradient-to-br from-white via-amber-50/70 to-orange-50 p-6 text-left transition hover:-translate-y-1 hover:border-amber-300 hover:shadow-xl"
      >
        <div className="flex items-center justify-between">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white border border-amber-100 text-amber-700 shadow-sm">
            <FiPlus className="text-xl" />
          </div>
          <span className="text-xs font-semibold uppercase tracking-[0.24em] text-amber-700">
            Slot {index + 1}
          </span>
        </div>
        <div className="mt-10">
          <div className="text-2xl font-black text-slate-900">
            Add {meta.singular}
          </div>
          <p className="mt-3 text-sm leading-6 text-slate-600">
            Open the same listing flow from this empty slot and publish your
            next {meta.singular.toLowerCase()}.
          </p>
        </div>
        <div className="mt-8 inline-flex items-center rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white">
          Create now
        </div>
        {!locked ? (
          <p className="mt-4 text-xs text-slate-500">
            You can still change your store type until your first listing is
            submitted.
          </p>
        ) : null}
      </button>
    );
  }

  const metricItems = [
    {
      label: "Price",
      value: formatListingPrice(item),
    },
    {
      label: "Affiliate",
      value: item.affiliateEnabled ? "Enabled" : "Off",
    },
    ...(isProductCard
      ? [
          {
            label: "Stock",
            value: formatListingStock(item),
          },
          {
            label: "Wholesale",
            value: getWholesaleSummary(item),
          },
        ]
      : []),
  ];

  return (
    <div className="min-h-[280px] overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl">
      <div className="relative h-36 bg-slate-100">
        <SmartImage
          src={listingImage(item)}
          alt={item.title || meta.singular}
        />
        <div className="absolute inset-x-0 top-0 flex items-start justify-between p-4">
          <span className="rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm">
            Slot {index + 1}
          </span>
          <span
            className={`rounded-full px-3 py-1 text-xs font-semibold shadow-sm ${statusTone(item.status)}`}
          >
            {item.status || "draft"}
          </span>
        </div>
      </div>
      <div className="flex h-[calc(100%-9rem)] flex-col p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="line-clamp-2 text-lg font-black leading-tight text-slate-900">
              {item.title || `${meta.singular} ${index + 1}`}
            </div>
            <div className="mt-2 text-sm text-slate-500">
              {meta.singular} slot is active in your store.
            </div>
          </div>
          <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-sm">
            <Icon className="text-lg" />
          </div>
        </div>

        <div
          className={`mt-5 grid gap-3 text-sm ${
            isProductCard ? "grid-cols-2" : "grid-cols-1 sm:grid-cols-3"
          }`}
        >
          {metricItems.map((metric) => (
            <div
              key={metric.label}
              className="min-h-[96px] rounded-2xl bg-slate-50 px-4 py-3"
            >
              <div className="text-xs uppercase tracking-[0.18em] text-slate-400">
                {metric.label}
              </div>
              <div className="mt-2 break-words text-[15px] font-semibold leading-8 text-slate-800 sm:text-[17px]">
                {metric.value}
              </div>
            </div>
          ))}
          <div
            className={`rounded-2xl bg-slate-50 px-4 py-3 ${
              isProductCard ? "col-span-2" : ""
            }`}
          >
            <div className="text-xs uppercase tracking-[0.18em] text-slate-400">
              Storefront
            </div>
            <div className="mt-1 font-semibold text-slate-800">
              {isProductCard ? (active ? "Active" : "Inactive") : "Live"}
            </div>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-2 pt-1">
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
          >
            <FiEdit2 />
            Edit
          </button>
          <button
            type="button"
            onClick={onAnalytics}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700"
          >
            <FiBarChart2 />
            Insights
          </button>
          {isProductCard ? (
            <button
              type="button"
              onClick={onToggleActive}
              className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold ${
                active
                  ? "border-amber-200 text-amber-700"
                  : "border-emerald-200 text-emerald-700"
              }`}
            >
              {active ? "Deactivate" : "Activate"}
            </button>
          ) : null}
          <button
            type="button"
            onClick={onDelete}
            className="inline-flex items-center gap-2 rounded-full border border-rose-200 px-4 py-2 text-sm font-semibold text-rose-600"
          >
            <FiTrash2 />
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ManageListings() {
  const [showAddModal, setShowAddModal] = useState(false);
  const [items, setItems] = useState([]);
  const [editData, setEditData] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [userDoc, setUserDoc] = useState(null);
  const [storeDoc, setStoreDoc] = useState(null);
  const [authUser, setAuthUser] = useState(null);
  const [draftType, setDraftType] = useState("");

  const auth = getAuth();

  useEffect(
    () => onAuthStateChanged(auth, (u) => setAuthUser(u || null)),
    [auth],
  );

  useEffect(() => {
    if (!authUser?.uid) return;
    return onSnapshot(doc(db, "users", authUser.uid), (snap) => {
      setUserDoc(snap.exists() ? snap.data() : null);
    });
  }, [authUser?.uid]);

  useEffect(() => {
    if (!authUser?.uid) return;
    return onSnapshot(doc(db, "stores", authUser.uid), (snap) => {
      setStoreDoc(snap.exists() ? snap.data() : null);
    });
  }, [authUser?.uid]);

  useEffect(() => {
    if (!authUser?.uid) return;
    const qItems = query(
      collection(db, "courses"),
      where("createdBy", "==", authUser.uid),
    );
    return onSnapshot(qItems, (snapshot) => {
      setItems(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
  }, [authUser?.uid]);

  const counts = useMemo(() => {
    const course = items.filter(
      (i) => String(i.type || "").toLowerCase() === "course",
    ).length;
    const product = items.filter(
      (i) => String(i.type || "").toLowerCase() === "product",
    ).length;
    const service = items.filter(
      (i) => String(i.type || "").toLowerCase() === "service",
    ).length;
    return { course, product, service };
  }, [items]);

  const packageState = useMemo(
    () => summarizeStorePackages(userDoc || {}),
    [userDoc],
  );
  const activePkg = packageState.hasActivePackage ? packageState.summary : null;
  const storeBusinessType = useMemo(
    () => inferStoreBusinessType(userDoc || {}, items),
    [items, userDoc],
  );

  useEffect(() => {
    if (storeBusinessType) {
      setDraftType(storeBusinessType);
    } else if (!items.length) {
      setDraftType((prev) => prev || "");
    }
  }, [items.length, storeBusinessType]);

  const limits = useMemo(
    () => ({
      courseLimit: Number(activePkg?.courseLimit || 0),
      productLimit: Number(activePkg?.productLimit || 0),
      serviceLimit: Number(activePkg?.serviceLimit || 0),
      hasActive: packageState.hasActivePackage,
    }),
    [activePkg, packageState.hasActivePackage],
  );

  const canOpenModal = userDoc?.status === "approved" && limits.hasActive;
  const hasProductBankDetails = useMemo(
    () => hasRequiredPaymentDetails(userDoc || {}, storeDoc || {}),
    [storeDoc, userDoc],
  );
  const selectedType = storeBusinessType || draftType || "";
  const selectedMeta = TYPE_META[selectedType] || null;
  const selectedItems = useMemo(
    () =>
      selectedType
        ? items.filter(
            (i) => String(i.type || "").toLowerCase() === selectedType,
          )
        : [],
    [items, selectedType],
  );
  const selectedLimit = limitForType(limits, selectedType);
  const selectedStats = useMemo(() => {
    const liveCount = selectedItems.filter((item) => {
      const status = String(item?.status || "").toLowerCase();
      return status === "approved" || status === "live";
    }).length;
    const draftCount = selectedItems.filter((item) => {
      const status = String(item?.status || "").toLowerCase();
      return !["approved", "live"].includes(status);
    }).length;
    const remaining =
      selectedLimit > 0
        ? Math.max(selectedLimit - selectedItems.length, 0)
        : null;
    return {
      used: selectedItems.length,
      live: liveCount,
      drafts: draftCount,
      remaining,
    };
  }, [selectedItems, selectedLimit]);
  const slotCount = selectedType
    ? selectedLimit > 0
      ? Math.max(selectedLimit, selectedItems.length)
      : Math.max(selectedItems.length + 1, 6)
    : 0;
  const slotItems = useMemo(
    () =>
      Array.from(
        { length: slotCount },
        (_, index) => selectedItems[index] || null,
      ),
    [selectedItems, slotCount],
  );
  const canChangeDraftType = !storeBusinessType && selectedItems.length === 0;

  async function handleDelete() {
    setDeleting(true);
    try {
      if (
        deleteTarget?.pdf &&
        typeof deleteTarget.pdf === "string" &&
        deleteTarget.pdf.startsWith("https://")
      ) {
        const url = new URL(deleteTarget.pdf);
        const path = decodeURIComponent(
          url.pathname.replace(/^\/v0\/b\/[^/]+\/o\//, ""),
        );
        await deleteObject(ref(storage, path)).catch(() => {});
      }
      await deleteDoc(doc(db, "courses", deleteTarget.id));
      setDeleteTarget(null);
    } catch {
      alert("Error deleting. Try again.");
    }
    setDeleting(false);
  }

  async function handleToggleProductActive(item) {
    if (!item?.id || String(item.type || "").toLowerCase() !== "product")
      return;
    const nextActive = item.isActive === false;
    try {
      await updateDoc(doc(db, "courses", item.id), {
        isActive: nextActive,
        updatedAt: serverTimestamp(),
      });
      showToast(
        nextActive
          ? "Product activated. It is visible in your shop again."
          : "Product deactivated. It is now hidden from buyers.",
        {
          type: "success",
        },
      );
    } catch (error) {
      console.error("product active toggle failed", error);
      showToast("Couldn't update this product right now. Please try again.", {
        type: "error",
      });
    }
  }

  const openCreateModal = (type) => {
    if (!type) return;
    if (!canOpenModal) return;
    if (type === "product" && !hasProductBankDetails) {
      showToast("Please add your bank details and try again.", {
        type: "error",
      });
      return;
    }
    setDraftType(type);
    setEditData(null);
    setShowAddModal(true);
  };

  if (authUser === null) {
    return <div className="max-w-6xl mx-auto py-8 px-4">Loading…</div>;
  }

  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      <div className="overflow-hidden rounded-[32px] border border-amber-200 bg-gradient-to-br from-[#fff9ef] via-white to-[#f5ede2] shadow-[0_24px_70px_rgba(173,120,41,0.14)]">
        <div className="grid gap-8 p-6 md:grid-cols-[1.15fr,0.85fr] md:p-8">
          <div>
            <div className="inline-flex rounded-full border border-amber-200 bg-white/80 px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-amber-700">
              Store Builder
            </div>
            <h1 className="mt-5 text-3xl font-black tracking-tight text-slate-900 md:text-5xl">
              Manage your shop owner listings with a guided slot layout.
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-7 text-slate-600 md:text-lg">
              Select the store type you want to build, then fill the package
              slots one by one. After your first listing is added, that store
              type becomes locked for this store.
            </p>
            <div className="mt-6 flex flex-wrap gap-3 text-sm">
              <span className="rounded-full bg-white px-4 py-2 font-semibold text-slate-700 shadow-sm">
                Package: {activePkg?.name || "No active package"}
              </span>
              <span className="rounded-full bg-white px-4 py-2 font-semibold text-slate-700 shadow-sm">
                Store mode: {getBusinessTypeLabel(storeBusinessType)}
              </span>
              <span className="rounded-full bg-white px-4 py-2 font-semibold text-slate-700 shadow-sm">
                Current listings: {items.length}
              </span>
            </div>
          </div>

          <div className="rounded-[28px] border border-white/80 bg-white/80 p-6 shadow-sm backdrop-blur">
            <div className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">
              Package Summary
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-3 md:grid-cols-1 xl:grid-cols-3">
              <div className="rounded-2xl bg-slate-900 px-4 py-4 text-white">
                <div className="text-xs uppercase tracking-[0.18em] text-white/60">
                  Courses
                </div>
                <div className="mt-2 text-3xl font-black">
                  {limits.courseLimit || "∞"}
                </div>
              </div>
              <div className="rounded-2xl bg-slate-900 px-4 py-4 text-white">
                <div className="text-xs uppercase tracking-[0.18em] text-white/60">
                  Products
                </div>
                <div className="mt-2 text-3xl font-black">
                  {limits.productLimit || "∞"}
                </div>
              </div>
              <div className="rounded-2xl bg-slate-900 px-4 py-4 text-white">
                <div className="text-xs uppercase tracking-[0.18em] text-white/60">
                  Services
                </div>
                <div className="mt-2 text-3xl font-black">
                  {limits.serviceLimit || "∞"}
                </div>
              </div>
            </div>

            <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm leading-6 text-amber-900">
              {storeBusinessType ? (
                <>
                  Your store is locked to{" "}
                  <span className="font-bold">
                    {TYPE_META[storeBusinessType]?.label || storeBusinessType}
                  </span>
                  . To change into another store type later, you need to start a
                  new store path with a new package flow.
                </>
              ) : (
                <>
                  Until you add your first listing, you can freely switch
                  between Courses, Products, and Services. Once the first one is
                  submitted, the type is locked.
                </>
              )}
            </div>

            {selectedMeta ? (
              <div className="mt-5 rounded-[24px] border border-slate-200 bg-slate-50 px-4 py-4">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Selected {selectedMeta.label} summary
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl bg-white px-4 py-3 shadow-sm">
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-400">
                      Used slots
                    </div>
                    <div className="mt-1 text-2xl font-black text-slate-900">
                      {selectedStats.used}
                    </div>
                  </div>
                  <div className="rounded-2xl bg-white px-4 py-3 shadow-sm">
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-400">
                      Remaining slots
                    </div>
                    <div className="mt-1 text-2xl font-black text-slate-900">
                      {selectedStats.remaining == null
                        ? "∞"
                        : selectedStats.remaining}
                    </div>
                  </div>
                  <div className="rounded-2xl bg-white px-4 py-3 shadow-sm">
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-400">
                      Live items
                    </div>
                    <div className="mt-1 text-2xl font-black text-slate-900">
                      {selectedStats.live}
                    </div>
                  </div>
                  <div className="rounded-2xl bg-white px-4 py-3 shadow-sm">
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-400">
                      Pending / other
                    </div>
                    <div className="mt-1 text-2xl font-black text-slate-900">
                      {selectedStats.drafts}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {!canOpenModal ? (
              <div className="mt-4 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-4 text-sm leading-6 text-sky-900">
                {userDoc?.status !== "approved"
                  ? "Your account is not approved yet. Contact admin before posting listings."
                  : "You need an active store package before you can start filling slots."}
              </div>
            ) : null}

            {canOpenModal &&
            selectedType === "product" &&
            !hasProductBankDetails ? (
              <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-4 text-sm leading-6 text-rose-700">
                Please add your bank details and try again before creating
                products.{" "}
                <Link to="/profile" className="font-semibold underline">
                  Open Profile settings
                </Link>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="mt-8 rounded-[30px] border border-slate-200 bg-white p-6 shadow-sm md:p-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-400">
              Step 1
            </div>
            <h2 className="mt-2 text-2xl font-black text-slate-900">
              Choose your store type
            </h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
              Pick the business model you want this store to follow. After the
              first listing is created, this selection becomes fixed for the
              store.
            </p>
          </div>
          {selectedType && canOpenModal ? (
            <button
              type="button"
              onClick={() => openCreateModal(selectedType)}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold text-white shadow-sm ${
                selectedMeta?.button || "bg-slate-900 hover:bg-black"
              }`}
            >
              <FiPlus />
              Add {selectedMeta?.singular || "Listing"}
            </button>
          ) : null}
        </div>

        <div className="mt-6 grid gap-5 lg:grid-cols-3">
          {Object.keys(TYPE_META).map((typeKey) => (
            <TypeChoiceCard
              key={typeKey}
              type={typeKey}
              limit={limitForType(limits, typeKey)}
              active={selectedType === typeKey}
              locked={storeBusinessType === typeKey}
              disabled={Boolean(
                storeBusinessType && storeBusinessType !== typeKey,
              )}
              onSelect={(type) => setDraftType(type)}
            />
          ))}
        </div>
      </div>

      {selectedType ? (
        <div className="mt-8 rounded-[30px] border border-slate-200 bg-white p-6 shadow-sm md:p-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <div className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-400">
                Step 2
              </div>
              <h2 className="mt-2 text-2xl font-black text-slate-900">
                Fill your {selectedMeta?.label} slots
              </h2>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
                {canChangeDraftType
                  ? `Your first ${selectedMeta?.singular.toLowerCase()} has not been added yet, so you can still switch store type above.`
                  : `This store now follows the ${selectedMeta?.label.toLowerCase()} path. Keep filling the remaining slots from this same business type.`}
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <span className="rounded-full bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700">
                Used: {selectedItems.length}
              </span>
              <span className="rounded-full bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700">
                Capacity: {selectedLimit > 0 ? selectedLimit : "Unlimited"}
              </span>
              {!storeBusinessType ? (
                <span className="rounded-full bg-amber-100 px-4 py-2 text-sm font-semibold text-amber-800">
                  Type still changeable
                </span>
              ) : (
                <span className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white">
                  Type locked
                </span>
              )}
            </div>
          </div>

          <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {slotItems.map((item, index) => (
              <ListingSlotCard
                key={item?.id || `empty-${index}`}
                index={index}
                type={selectedType}
                item={item}
                locked={Boolean(storeBusinessType)}
                onCreate={() => openCreateModal(selectedType)}
                onEdit={() => {
                  setEditData(prepareEditData(item));
                  setShowAddModal(true);
                }}
                onAnalytics={() => setAnalyticsData(item)}
                onToggleActive={() => handleToggleProductActive(item)}
                onDelete={() => setDeleteTarget(item)}
              />
            ))}
          </div>

          {selectedItems.length > 0 ? (
            <div className="mt-10 rounded-[28px] border border-slate-100 bg-slate-50/60 p-5 md:p-6">
              <div className="mb-4 text-lg font-black text-slate-900">
                Detailed listing table
              </div>
              <CourseProductTable
                data={selectedItems}
                onEdit={(item) => {
                  setEditData(prepareEditData(item));
                  setShowAddModal(true);
                }}
                onDelete={setDeleteTarget}
                onAnalytics={(item) => setAnalyticsData(item)}
                onToggleActive={handleToggleProductActive}
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {showAddModal && (
        <AddCourseProductModal
          onClose={() => {
            setShowAddModal(false);
            setEditData(null);
          }}
          editData={editData}
          initialType={selectedType || ""}
          storeLimits={limits}
          storeCounts={counts}
          storeBusinessType={storeBusinessType}
          productBankReady={hasProductBankDetails}
        />
      )}

      {analyticsData && (
        <CourseAnalyticsDrawer
          open={!!analyticsData}
          course={analyticsData}
          onClose={() => setAnalyticsData(null)}
        />
      )}

      {deleteTarget && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full text-center">
            <div className="mb-4 text-xl font-bold text-red-600">
              Delete {deleteTarget.title}?
            </div>
            <div className="mb-6 text-gray-500">
              This action <b>cannot be undone</b>. Are you sure?
            </div>
            <div className="flex gap-3 justify-center">
              <button
                className="bg-gray-100 px-4 py-2 rounded font-semibold"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="bg-red-600 text-white px-6 py-2 rounded font-semibold shadow hover:bg-red-700 transition"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
